import { Component, inject } from '@angular/core';
import { SharedModule } from '../../../shared/shared.module';
import { ModalBaseComponent } from '../../../shared/components/modal-base/modal-base.component';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  CustomField,
  CustomFieldType,
  CustomFieldTypeHumanized,
} from '../../../core/models/entities/custom-field';
import { InputOptionsHelper } from '../../../core/helpers/input-options.helper';
import {
  BookableBy,
  BookableByHumanized,
} from '../../../core/models/common/bookable-by';
import { SharedManagerModule } from '../../../shared-manager/shared-manager.module';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiError } from '../../../core/models/common/api-error';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { Store } from '@ngrx/store';
import { customFieldsActions } from 'angular/app/core/store/custom-fields';
import { CustomFieldsService } from '../../../core/services/custom-fields.service';
import { StoreCustomFieldRequest } from '../../../core/requests/custom-fields/store-custom-field.request';
import { ToastService } from '../../../core/services/toast.service';
import { ModalService } from '../../../core/services/modal.service';
import { UpdateCustomFieldRequest } from '../../../core/requests/custom-fields/update-custom-field.request';

@Component({
  selector: 'app-create-custom-field-modal',
  imports: [SharedModule, SharedManagerModule],
  template: `
    <app-modal [modalInstance]="modalInstance" (onOk)="submit()">
      @if (editingCustomField) {
        <app-alert variant="neutral">
          Vous modifiez un champ personnalisé existant.
          <span class="font-medium"
            >Seules les nouvelles réservations seront affectées.</span
          >
          Les anciennes conserveront les valeurs précédentes.
        </app-alert>

        <div class="mt-4"></div>
      }

      <form novalidate>
        <app-select-input
          [control]="form.controls.type"
          name="type"
          label="Type de champ"
          [options]="typeOptions"
        ></app-select-input>

        <app-text-input
          [control]="form.controls.title"
          name="title"
          label="Titre"
          helpText="Ce titre sera affiché dans le formulaire de réservation, en-dessus du champ à remplir."
        ></app-text-input>

        @if (form.controls.type.value === CustomFieldType.Dropdown) {
          <div class="font-medium">Options à choix</div>

          <div class="space-y-2">
            @for (option of form.controls.options.controls; track option) {
              <div class="flex gap-4">
                <div class="w-2/3 flex-none">
                  <app-text-input
                    [control]="option"
                    [name]="'options.' + $index"
                    [marginBottom]="false"
                  ></app-text-input>
                </div>
                @if (form.controls.options.controls.length > 2) {
                  <div
                    class="translate-y-4 cursor-pointer"
                    (click)="removeOption($index)"
                  >
                    <i class="fal fa-times"></i>
                  </div>
                }
              </div>
            }
          </div>

          <div class="mt-4 cursor-pointer space-x-2" (click)="addOption()">
            <i class="fal fa-plus"></i>
            <a>Ajouter une option</a>
          </div>

          <div class="mt-6"></div>
        }

        <app-text-input
          [control]="form.controls.help_text"
          name="help_text"
          label="Texte d'aide"
          helpText="Ce texte sera affiché sous le champ, afin de guider l'utilisateur."
        ></app-text-input>

        <app-radio-input
          [control]="form.controls.is_required"
          label="Est-ce que le champ est obligatoire ?"
          variant="native"
          name="required"
          [helpText]="
            form.controls.type.value === CustomFieldType.Checkbox
              ? 'Si le champ est obligatoire, l\\'utilisateur devra cocher la case pour valider la réservation.'
              : undefined
          "
          [inline]="true"
          [options]="[
            { value: true, label: 'Oui' },
            { value: false, label: 'Non' },
          ]"
        ></app-radio-input>

        <app-radio-input
          [control]="form.controls.visibility"
          name="visibility"
          label="Pour quel type de réservation le champ doit-il s'afficher ?"
          variant="native"
          [inline]="true"
          [options]="visibilityOptions"
        >
        </app-radio-input>

        <app-user-rooms-access
          [allRoomsAccessControl]="form.controls.valid_for_all_rooms"
          [assignedRoomsIdsControl]="form.controls.room_ids"
          [allRoomsAccessOptions]="roomsLimitOptions"
          [filterRoomsByVisibility]="
            form.controls.visibility.value === BookableBy.Public
              ? [BookableBy.Public, BookableBy.Both]
              : form.controls.visibility.value === BookableBy.Private
                ? [BookableBy.Private, BookableBy.Both]
                : [BookableBy.Public, BookableBy.Private, BookableBy.Both]
          "
          radioInputLabel="Pour quelles salles le champ doit-il s'afficher ?"
          radioInputVariant="native"
        />
      </form>
    </app-modal>
  `,
})
export class CustomFieldFormModal extends ModalBaseComponent {
  protected store = inject(Store);
  protected toastService = inject(ToastService);
  protected modalService = inject(ModalService);
  protected customFieldsService = inject(CustomFieldsService);
  protected form = new FormGroup({
    title: new FormControl('', [Validators.required]),
    type: new FormControl<CustomFieldType>(CustomFieldType.Text, [
      Validators.required,
    ]),
    is_required: new FormControl<boolean>(false),
    help_text: new FormControl(''),
    options: new FormArray([]),
    visibility: new FormControl('', [Validators.required]),
    valid_for_all_rooms: new FormControl(null),
    room_ids: new FormControl([]),
    position: new FormControl(null),
  });
  protected typeOptions = InputOptionsHelper.fromEnum(
    CustomFieldType,
    CustomFieldTypeHumanized,
  );
  protected visibilityOptions = InputOptionsHelper.fromEnum(
    BookableBy,
    BookableByHumanized,
  );
  protected roomsLimitOptions = [
    { value: true, label: 'Toutes' },
    { value: false, label: 'Limité' },
  ];
  protected formValidationService = inject(FormValidationService);
  protected editingCustomField: CustomField | null = null;
  protected readonly CustomFieldType = CustomFieldType;
  protected readonly BookableBy = BookableBy;

  constructor() {
    super();

    this.form.controls.type.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((type: CustomFieldType) => {
        if (this.editingCustomField) {
          return;
        }

        if (type === CustomFieldType.Dropdown) {
          this.addOption(2);
        } else {
          this.removeAllOptions();
        }

        this.form.controls.options.updateValueAndValidity();
      });

    this.form.controls.valid_for_all_rooms.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((validForAllRooms) => {
        if (validForAllRooms) {
          this.form.controls.room_ids.clearValidators();
          this.form.controls.room_ids.setValue([], { emitEvent: false });
        } else {
          this.form.controls.room_ids.setValidators([
            Validators.required,
            Validators.minLength(1),
          ]);
        }
        this.form.controls.room_ids.updateValueAndValidity();
      });

    this.form.controls.valid_for_all_rooms.patchValue(true);
  }

  protected addOption(count = 1): void {
    for (let i = 0; i < count; i++) {
      (this.form.controls.options as FormArray).push(
        new FormControl('', [Validators.required]),
      );
    }
  }

  protected removeOption(index: number): void {
    (this.form.controls.options as FormArray).removeAt(index);
  }

  private removeAllOptions(): void {
    while ((this.form.controls.options as FormArray).length) {
      (this.form.controls.options as FormArray).removeAt(0);
    }
  }

  setEditingCustomField(customField: CustomField): void {
    this.editingCustomField = customField;
    for (const option of customField.options || []) {
      this.addOption();
    }

    this.form.patchValue({
      title: customField.title,
      type: customField.type,
      is_required: customField.is_required,
      help_text: customField.help_text,
      visibility: customField.visibility,
      room_ids: customField.room_ids || [],
      options: customField.options || [],
      valid_for_all_rooms: !customField.room_ids?.length,
      position: customField.position,
    });
  }

  protected async submit(): Promise<void> {
    if (!this.formValidationService.validate(this.form)) return;

    try {
      this.modalInstance.saving = true;
      const formData = this.form.value as StoreCustomFieldRequest;
      if (formData.options?.length === 0) {
        formData.options = null;
      }
      if (formData.room_ids?.length === 0) {
        formData.room_ids = null;
      }

      if (this.editingCustomField) {
        const customField: CustomField = await this.customFieldsService.update(
          this.editingCustomField.id,
          this.form.value as StoreCustomFieldRequest,
        );
        this.store.dispatch(
          customFieldsActions.removeOne({ id: this.editingCustomField.id }),
        );
        this.store.dispatch(customFieldsActions.upsertOne({ customField }));
      } else {
        const customField: CustomField = await this.customFieldsService.store(
          this.form.value as UpdateCustomFieldRequest,
        );
        this.store.dispatch(customFieldsActions.upsertOne({ customField }));
      }

      this.toastService.success(
        'Le champ personnalisé a été enregistré avec succès.',
      );
      this.modalService.close();
    } catch (exception) {
      this.formValidationService.displayApiValidationErrors(
        this.form,
        exception as ApiError,
      );
    }
    this.modalInstance.saving = false;
  }
}
